import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Sitemap } from '../components/Sitemap';
import { LearningUnitContent } from '../components/LearningUnitContent';
import { useLearningData } from '../context/LearningDataContext';
import { LessonCard } from '../components/LessonCard';
import { useToast } from '../components/ui/ToastContainer';
import { BookIcon, ChevronRightIcon, ArrowLeftIcon, LockIcon } from 'lucide-react';
export function LearningUnitPage() {
  const {
    isArabic
  } = useLanguage();
  const {
    unitId
  } = useParams<{
    unitId: string;
  }>();
  const navigate = useNavigate();
  const toast = useToast();
  const {
    learningData
  } = useLearningData();
  const [unitData, setUnitData] = useState<any>(null);
  const [courseData, setCourseData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!unitId || !learningData) {
      setLoading(false);
      return;
    }
    try {
      const unitIdNum = parseInt(unitId);
      // Find course that contains this unit
      const course = learningData.find((course: any) => course.units && course.units.some((unit: any) => unit.id === unitIdNum));
      if (!course) {
        toast.error(isArabic ? 'خطأ في التحميل' : 'Loading Error', isArabic ? 'لم يتم العثور على الدورة' : 'Course not found');
        navigate('/learn');
        return;
      }
      // Find unit
      const unit = course.units.find((unit: any) => unit.id === unitIdNum);
      if (!unit) {
        toast.error(isArabic ? 'خطأ في التحميل' : 'Loading Error', isArabic ? 'لم يتم العثور على الوحدة' : 'Unit not found');
        navigate('/learn');
        return;
      }
      setCourseData(course);
      setUnitData(unit);
    } catch (error) {
      console.error('Error loading unit data:', error);
      toast.error(isArabic ? 'خطأ في التحميل' : 'Loading Error', isArabic ? 'حدث خطأ أثناء تحميل البيانات' : 'Error loading data');
      navigate('/learn');
    } finally {
      setLoading(false);
    }
  }, [unitId, learningData, navigate, toast, isArabic]);
  const handleLessonClick = (lessonId: number) => {
    navigate(`/learn/unit/${unitId}/lesson/${lessonId}`);
  };
  if (loading) {
    return <div className="w-full px-6 md:px-10 lg:px-20 py-16" data-id="element-3098">
        <div className="max-w-4xl mx-auto text-center" data-id="element-3099">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" data-id="element-3100"></div>
          <p className="text-gray-600 dark:text-gray-300" data-id="element-3101">
            {isArabic ? 'جارِ التحميل...' : 'Loading...'}
          </p>
        </div>
      </div>;
  }
  if (!unitData) {
    return <div className="w-full px-6 md:px-10 lg:px-20 py-16" data-id="element-3102">
        <div className="max-w-4xl mx-auto text-center" data-id="element-3103">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4" data-id="element-3104">
            {isArabic ? 'لم يتم العثور على الوحدة' : 'Unit Not Found'}
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-6" data-id="element-3105">
            {isArabic ? 'لم نتمكن من العثور على الوحدة المطلوبة' : "We couldn't find the requested unit"}
          </p>
          <button onClick={() => navigate('/learn')} className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" data-id="element-3106">
            {isArabic ? 'العودة إلى صفحة التعلم' : 'Back to Learning Page'}
          </button>
        </div>
      </div>;
  }
  return <div className="w-full px-6 md:px-10 lg:px-20 py-16" data-id="element-3107">
      <Sitemap currentPage={{
      en: `Unit ${unitData.id}: ${unitData.title.en}`,
      ar: `الوحدة ${unitData.id}: ${unitData.title.ar}`
    }} breadcrumbs={[{
      label: {
        en: 'Learn',
        ar: 'تعلم'
      },
      href: '/learn'
    }, {
      label: {
        en: courseData.title.en,
        ar: courseData.title.ar
      },
      href: `/learn/course/${courseData.id}`
    }]} data-id="element-3108" />
      <div className="max-w-4xl mx-auto" data-id="element-3109">
        <div className="mb-12" data-id="element-3110">
          <button onClick={() => navigate('/learn')} className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors mb-4" data-id="element-3111">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-3112" />
            <span data-id="element-3113">
              {isArabic ? 'العودة إلى المسار التعليمي' : 'Back to Learning Path'}
            </span>
          </button>
          <div className="flex items-center gap-4 mb-6" data-id="element-3114">
            <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg" data-id="element-3115">
              <BookIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" data-id="element-3116" />
            </div>
            <div data-id="element-3117">
              <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-3118">
                {isArabic ? unitData.title.ar : unitData.title.en}
              </h1>
              <p className="text-xl text-gray-600 dark:text-gray-300" data-id="element-3119">
                {isArabic ? unitData.description.ar : unitData.description.en}
              </p>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 mb-8" data-id="element-3120">
            <div className="flex items-center justify-between mb-4" data-id="element-3121">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white" data-id="element-3122">
                {isArabic ? 'تقدمك في الوحدة' : 'Unit Progress'}
              </h2>
              <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-3123">
                {unitData.progress}%
              </span>
            </div>
            <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-3124">
              <div className="h-full bg-blue-600 dark:bg-blue-400 transition-all duration-300" style={{
              width: `${unitData.progress}%`
            }} data-id="element-3125" />
            </div>
          </div>
        </div>
        <div className="mb-12" data-id="element-3126">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6" data-id="element-3127">
            {isArabic ? 'محتوى الوحدة' : 'Unit Content'}
          </h2>
          <div className="space-y-4" data-id="element-3128">
            {unitData.lessons.map((lesson: any) => <LessonCard key={lesson.id} lesson={lesson} onLessonClick={handleLessonClick} data-id="element-3129" />)}
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-8" data-id="element-3130">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6" data-id="element-3131">
            {isArabic ? 'تعلم المحتوى التفاعلي' : 'Interactive Learning Content'}
          </h2>
          <LearningUnitContent data-id="element-3132" />
        </div>
      </div>
    </div>;
}